/* eslint react/prop-types: 0 */
import 'whatwg-fetch';

import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import Autosuggest from 'react-autosuggest';
import Select from 'react-select';
import invert from 'lodash/invert';
import debounce from 'lodash/debounce';

import SegmentClient from '../services/SegmentClient';
import { SuggesterClient } from '../services/SearchClient';
import { iterUnpack } from '../services/APIClient';
import PlaceholderWithIcon from '../components/image_search/Common';
import { BASE_URL, POPULAR_IMAGES } from '../components/image_search/Constants';


// Styles
const buttonClasses = 'white pt-xxs pr-s pb-xs pl-s';
const activeButtonClasses = 'black pt-xs pr-s pb-xs pl-s bg-gunmetal-300';
const buttonStyles = {
  background: 'rgba(0, 0, 0, 0.51)',
  minWidth: '8rem',
  borderRadius: '5px 5px 0 0',
  display: 'inline-block',
};
const activeButtonStyles = {
  minWidth: '8rem',
  borderRadius: '5px 5px 0 0',
  display: 'inline-block',
};

const SuggestionTheme = {
  container: {
    position: 'relative',
  },
  suggestionsContainer: {
    position: 'absolute',
    right: 0,
    left: 0,
    zIndex: 1,
    background: 'white',
    listStyleType: 'none',
  },
  suggestionsList: {
    margin: 0,
    padding: 0,
    boxShadow: '0 0 5px #eee',
  },
  suggestion: {
    listStyleType: 'none',
    textAlign: 'left',
    padding: '0.5rem 0 0.5rem 1rem',
    fontSize: '1.25rem',
  },
  suggestionHighlighted: {
    background: '#eee',
    cursor: 'pointer',
  },
  input: {
    appearance: 'none',
    backgroundColor: 'white',
    border: '1px solid white',
    boxSizing: 'border-box',
    color: 'black',
    display: 'inline-flex',
    flexGrow: '1',
    fontFamily: 'inherit',
    fontSize: '1.25rem',
    fontWeight: '400',
    height: '3.6rem',
    lineHeight: '2rem',
    margin: '0',
    paddingBottom: '0.5rem',
    paddingLeft: '1rem',
    paddingRight: '0.5rem',
    paddingTop: '0.5rem',
    transition: 'boxshadow 0.5s, border-color 0.25s ease-in-out',
    width: '100%',
  },
  inputFocused: {
    backgroundColor: 'white',
    outline: '0',
    transition: 'box-shadow 0.5s, border-color 0.25s ease-in-out',
  },
};

const inputWrapper = {
  backgroundColor: 'white',
  alignItems: 'center',
  borderRadius: '5px',
  boxShadow: '0 10px 32px rgba(0, 0, 0, 0.2)',
  display: 'flex',
  width: '100%',
};

const searchButton = {
  borderRadius: '0.2rem',
  backgroundColor: 'white',
  flexBasis: '0',
  height: '3.6rem',
  outline: 'none',
  marginBottom: '0',
  paddingRight: '1rem',
};

const searchIcon = {
  color: '#878787',
};

// Populate input field based on clicked suggestion
const getSuggestionValue = suggestion => suggestion.replace(/<(\/)?b>/g, '');

const renderSuggestion = suggestion => (
  <div
    key={'unique'}
    dangerouslySetInnerHTML={{ __html: suggestion }} // eslint-disable-line react/no-danger
  />
);


const renderSuggestionsContainer = ({ containerProps, children }) => (
  <div
    key={'unique'}
    {...containerProps}
  >{children}
  </div>
);

export default class HomepageSearchForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selected: 'projects',
      projects: {
        placeholder: 'Search architectural projects',
        endpoint: '/projects/q/q:',
      },
      images: {
        placeholder: 'Search architectural images',
        // TODO: set classification based on selected search term
        defaultEndpoint: `${BASE_URL}/building-features/`,
        popularImagesEndpoint: `${BASE_URL}/${POPULAR_IMAGES.slug}`,
        typeEndpoint: `${BASE_URL}/${POPULAR_IMAGES.slug}/type=`,
      },
      products: {
        placeholder: 'Search architectural products',
        endpoint: '/product-search?q=',
      },
      brands: {
        placeholder: 'Search manufacturers',
        endpoint: '/brands?q=',
      },
      value: '',
      suggestions: [],
      query: '',
    };
    this.segment = new SegmentClient();

    this.debouncedLoadSuggestions = debounce(this.doSearch, 500);
  }

  componentWillMount() {
    const props = iterUnpack(JSON.parse(window.props));
    this.setState({
      images: Object.assign(
        this.state.images,
        {
          options: (props.allImages.searchTerms || []).map(e => ({
            label: e,
            value: e,
          })),
          allTypes: invert({ ...(props.allImages.types || []) }),
        },
      ),
    });
  }

  onChange = (event, { newValue }) => {
    this.setState({
      value: newValue,
    });
  };

  onSuggestionsFetchRequested = ({ value }) => {
    this.debouncedLoadSuggestions(value);
  };

  onSuggestionsClearRequested = () => {
    this.setState({
      suggestions: [],
    });
  };

  onSubmit = () => {
    const { value, selected, projects, images, products, brands } = this.state;
    this.segment.track(
      'homepage query submitted',
      { query: value, type: selected },
    );

    let endpoint = '';
    let query = value;

    if (selected === 'projects') {
      endpoint = projects.endpoint;
    } else if (selected === 'images') {
      query = encodeURIComponent(value);
      if (value === POPULAR_IMAGES.text) {
        query = '';
        endpoint = images.popularImagesEndpoint;
      } else if (images.allTypes[value]) {
        endpoint = images.typeEndpoint;
      } else {
        endpoint = images.defaultEndpoint;
      }
    } else if (selected === 'products') {
      endpoint = products.endpoint;
    } else if (selected === 'brands') {
      endpoint = brands.endpoint;
    }

    window.location.href = `${endpoint}${query}`;
  };

  onInputChange = (event) => {
    this.setState({ value: event.target.value });
  }

  setSection = (section) => {
    this.setState({ selected: section });
  };

  doSearch = (value) => {
    const q = value || '';
    return SuggesterClient('suggestion_terms', q, 6)
      .then(response => response.json())
      .then((response) => {
        if ('error' in response.data) {
          const error = response.data.error;
          throw new Error(error);
        }
        const fetchedSuggestions = response.data.suggest.brand_count_v1[q].suggestions;
        const suggestionTerms = fetchedSuggestions.map(suggestion => suggestion.term);
        this.setState({ suggestions: suggestionTerms });
      })
      .catch((e) => {
        this.setState({
          error: e,
          loaded: true,
        });
      });
  };

  handleSubmit = (event) => {
    event.preventDefault();
    const { value } = this.state;
    this.onSubmit(value);
  };

  renderInputComponent = inputProps => (
    <div style={inputWrapper}>
      <input
        type="text"
        {...inputProps}
      />
      <button
        style={searchButton}
        onClick={this.handleSubmit}
      >
        <span
          style={searchIcon}
          className="material-icons"
        >search
        </span>
      </button>
    </div>
  )

  render() {
    const activeSection = this.state.selected;
    const { value, suggestions, images, brands, products } = this.state;
    const inputProps = {
      placeholder: activeSection === 'products' ? products.placeholder : brands.placeholder,
      suggestions,
      value,
      onChange: this.onChange,
    };

    return (
      <form key={'unique'} onSubmit={this.handleSubmit}>
        <div className="text-center">
          <a
            role="button"
            tabIndex={0}
            className={activeSection === 'projects' ? activeButtonClasses : buttonClasses}
            style={activeSection === 'projects' ? activeButtonStyles : buttonStyles}
            onClick={() => this.setSection('projects')}
          >Projects</a>
          <a
            role="button"
            tabIndex={0}
            className={activeSection === 'images' ? activeButtonClasses : buttonClasses}
            style={activeSection === 'images' ? activeButtonStyles : buttonStyles}
            onClick={() => this.setSection('images')}
          >Images</a>
          <a
            role="button"
            tabIndex={0}
            className={activeSection === 'products' ? activeButtonClasses : buttonClasses}
            style={activeSection === 'products' ? activeButtonStyles : buttonStyles}
            onClick={() => this.setSection('products')}
          >Products</a>
          <a
            role="button"
            tabIndex={0}
            className={activeSection === 'brands' ? activeButtonClasses : buttonClasses}
            style={activeSection === 'brands' ? activeButtonStyles : buttonStyles}
            onClick={() => this.setSection('brands')}
          >Manufacturers</a>
        </div>
        {activeSection === 'projects' &&
          <div
            key={'unique'}
            className="input-group"
            style={{
              marginBottom: '0',
            }}
          >
            <input
              value={value}
              onChange={this.onInputChange}
              className="border-white pl-s pr-s pt-s pb-s"
              style={{
                borderRadius: '3px',
                fontSize: '1.25rem',
                fontWeight: '400',
                marginBottom: '0',
                boxShadow: '0px 10px 32px rgba(0, 0, 0, 0.2)',
              }}
              type="text"
              placeholder={this.state[activeSection].placeholder}
            />
            <span
              className="input-group-label icon"
              style={{
                left: '-2.25rem',
                bottom: '0.1rem',
              }}
            >
              <a
                role="button"
                tabIndex={0}
                onClick={this.handleSubmit}
              >
                <span className="material-icons gray-400">search</span>
              </a>
            </span>
          </div>
        }
        {activeSection === 'images' &&
          <Select
            name="image-search-terms-selector"
            className="homepage-searchbar"
            placeholder={<PlaceholderWithIcon text="Search architectural images" />}
            onChange={option => this.setState({
              value: option.value,
            }, () => this.onSubmit())}
            value={value}
            options={images.options}
            clearable={false}
            arrowRenderer={() => null}
          />
        }
        {activeSection === 'brands' &&
          <Autosuggest
            suggestions={suggestions}
            onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
            onSuggestionsClearRequested={this.onSuggestionsClearRequested}
            onSuggestionSelected={this.handleSubmit}
            getSuggestionValue={getSuggestionValue}
            renderSuggestion={renderSuggestion}
            renderSuggestionsContainer={renderSuggestionsContainer}
            renderInputComponent={this.renderInputComponent}
            inputProps={inputProps}
            theme={SuggestionTheme}
          />
        }
        {activeSection === 'products' &&
          <Autosuggest
            suggestions={suggestions}
            onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
            onSuggestionsClearRequested={this.onSuggestionsClearRequested}
            onSuggestionSelected={this.handleSubmit}
            getSuggestionValue={getSuggestionValue}
            renderSuggestion={renderSuggestion}
            renderSuggestionsContainer={renderSuggestionsContainer}
            renderInputComponent={this.renderInputComponent}
            inputProps={inputProps}
            theme={SuggestionTheme}
          />
        }
      </form>
    );
  }
}

HomepageSearchForm.defaultProps = {
  brandPlaceholder: '',
  children: [],
  query: '',
  selected: '',
  suggestions: [],
  value: '',
};

HomepageSearchForm.propTypes = {
  brandPlaceholder: PropTypes.string,
  children: PropTypes.arrayOf(PropTypes.string),
  query: PropTypes.string,
  selected: PropTypes.string,
  suggestions: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.string,
};

ReactDOM.render(
  <HomepageSearchForm />,
  document.getElementById('HomepageSearchForm'),
);



// WEBPACK FOOTER //
// ./js/src/views/HomepageSearchForm.jsx